import { ImageUp, Type, X } from "lucide-react";
import type { PostContent } from "@/lib/types";

type ManualPostEditorProps = {
  post: PostContent;
  onChange: (updates: Partial<PostContent>) => void;
};

export function ManualPostEditor({ post, onChange }: ManualPostEditorProps) {
  const handleUpload = (
    field: "profileImage" | "profileHeaderImage" | "postImage",
    file: File | undefined,
  ) => {
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result === "string") {
        onChange({ [field]: reader.result } as Partial<PostContent>);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <section aria-labelledby="manual-editor-heading" className="space-y-4">
      <div className="flex items-center gap-2">
        <Type className="h-4 w-4 text-ocean" aria-hidden="true" />
        <h3 id="manual-editor-heading" className="text-sm font-semibold text-ink">
          Manual post editor
        </h3>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">Post text</span>
        <textarea
          value={post.text}
          onChange={(event) => onChange({ text: event.target.value })}
          placeholder="Paste the LinkedIn post text here..."
          rows={7}
          className="mt-2 w-full resize-y rounded-lg border border-slate-300 bg-white px-3 py-3 text-sm leading-6 text-ink shadow-sm transition placeholder:text-slate-400 hover:border-slate-400"
        />
      </label>

      <div className="grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Author name</span>
          <input
            value={post.authorName}
            onChange={(event) => onChange({ authorName: event.target.value })}
            className="mt-2 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-ink shadow-sm transition hover:border-slate-400"
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-slate-700">Timestamp</span>
          <input
            value={post.timestamp}
            onChange={(event) => onChange({ timestamp: event.target.value })}
            placeholder="2h"
            className="mt-2 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-ink shadow-sm transition hover:border-slate-400"
          />
        </label>
      </div>

      <label className="block">
        <span className="text-sm font-medium text-slate-700">
          Author headline
        </span>
        <input
          value={post.authorHeadline}
          onChange={(event) => onChange({ authorHeadline: event.target.value })}
          className="mt-2 min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-ink shadow-sm transition hover:border-slate-400"
        />
      </label>

      <div className="grid gap-4">
        <ImageInput
          label="Profile image"
          value={post.profileImage}
          previewShape="avatar"
          onUrlChange={(profileImage) => onChange({ profileImage })}
          onUpload={(file) => handleUpload("profileImage", file)}
          onClear={() => onChange({ profileImage: "" })}
        />
        <ImageInput
          label="Profile header image"
          value={post.profileHeaderImage}
          previewShape="wide"
          onUrlChange={(profileHeaderImage) => onChange({ profileHeaderImage })}
          onUpload={(file) => handleUpload("profileHeaderImage", file)}
          onClear={() => onChange({ profileHeaderImage: "" })}
        />
        <ImageInput
          label="Post attachment image"
          value={post.postImage}
          previewShape="wide"
          onUrlChange={(postImage) => onChange({ postImage })}
          onUpload={(file) => handleUpload("postImage", file)}
          onClear={() => onChange({ postImage: "" })}
        />
      </div>
    </section>
  );
}

function ImageInput({
  label,
  value,
  previewShape,
  onUrlChange,
  onUpload,
  onClear,
}: {
  label: string;
  value: string;
  previewShape: "avatar" | "wide";
  onUrlChange: (value: string) => void;
  onUpload: (file: File | undefined) => void;
  onClear: () => void;
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-slate-700">{label}</span>
        {value ? (
          <button
            type="button"
            onClick={onClear}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-100"
            aria-label={`Clear ${label.toLowerCase()}`}
            title={`Clear ${label.toLowerCase()}`}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
      <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
        <label className="block">
          <span className="sr-only">{label} URL</span>
          <input
            value={value.startsWith("data:") ? "" : value}
            onChange={(event) => onUrlChange(event.target.value)}
            placeholder="https://..."
            className="min-h-11 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-ink shadow-sm transition placeholder:text-slate-400 hover:border-slate-400"
            inputMode="url"
          />
        </label>
        <label className="inline-flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-ink transition hover:bg-slate-100">
          <ImageUp className="h-4 w-4" aria-hidden="true" />
          Upload
          <input
            type="file"
            accept="image/*"
            className="sr-only"
            onChange={(event) => onUpload(event.target.files?.[0])}
          />
        </label>
      </div>
      {value ? (
        <div
          className={`mt-3 overflow-hidden border border-slate-200 bg-white ${
            previewShape === "avatar"
              ? "h-14 w-14 rounded-full"
              : "aspect-[5/2] rounded-lg"
          }`}
        >
          <img
            src={value}
            alt=""
            className="h-full w-full object-cover"
            crossOrigin="anonymous"
            referrerPolicy="no-referrer"
          />
        </div>
      ) : null}
    </div>
  );
}
